// Geographic scatter — plot lat/lon points on a cartesian plane sized
// for the area of interest. v1 has NO basemap (no country outlines, no
// city streets); we render dots in lat/lon space with axis labels in
// degrees. For tightly-clustered data (e.g. all of SunMobility's
// Bengaluru vehicles near 77.59°E, 12.97°N) the cluster shape conveys
// the density question the user actually asked. World-wide data
// (multiple countries) renders as a rough world-shape scatter where
// the user can still see continental clusters.
//
// Future enhancement: register an ECharts world map and switch the
// coordinate system from cartesian to geo. Out of scope for this fix.
//
// Inputs:
//   lat, lon — required column names
//   value — optional; when present, drives marker size + color via
//           visualMap (the "high density" signal Claude usually wants).
//   label — optional; column whose value shows in the tooltip per point.

import type { ChartRow } from "../types";
import { columnIndex } from "./_shared";

interface Input {
  lat: string;
  lon: string;
  value?: string;
  label?: string;
}

export function buildGeoScatter(
  input: Input,
  rows: ChartRow[],
  columns: string[],
  _title: string,
  palette: ReadonlyArray<string>,
) {
  const latIdx = columnIndex(columns, input.lat);
  const lonIdx = columnIndex(columns, input.lon);
  const valIdx = input.value ? columnIndex(columns, input.value) : -1;
  const labelIdx = input.label ? columnIndex(columns, input.label) : -1;

  const FONT =
    '"Inter", "SF Pro Text", -apple-system, BlinkMacSystemFont, "Helvetica Neue", Arial, sans-serif';
  const AXIS_LABEL_COLOR = "#525252";
  const AXIS_LINE_COLOR = "#e5e7eb";
  const SPLIT_LINE_COLOR = "#f1f5f9";

  // ECharts wants scatter data as either [x, y] pairs or
  // {value: [x, y, ...], name: "..."} objects. Use objects so we can
  // attach a label per point for tooltips.
  let vMin = Infinity;
  let vMax = -Infinity;
  const points: Array<{ value: [number, number, number]; name: string }> = [];
  for (const row of rows) {
    const lat = Number(row[latIdx]);
    const lon = Number(row[lonIdx]);
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) continue;
    const v = valIdx >= 0 ? Number(row[valIdx] ?? 0) : 1;
    if (Number.isFinite(v)) {
      if (v < vMin) vMin = v;
      if (v > vMax) vMax = v;
    }
    const label = labelIdx >= 0 ? String(row[labelIdx] ?? "") : "";
    points.push({ value: [lon, lat, v], name: label });
  }
  if (!Number.isFinite(vMin)) vMin = 0;
  if (!Number.isFinite(vMax)) vMax = 1;

  // Compute reasonable axis bounds with a small padding so points aren't
  // jammed against the edges. Avoid fitting too tightly when all points
  // are near one location (Bengaluru cluster) — pad by at least 0.5° so
  // the cluster doesn't fill the entire chart edge to edge.
  const lonValues = points.map((p) => p.value[0]);
  const latValues = points.map((p) => p.value[1]);
  const lonLo = lonValues.length ? Math.min(...lonValues) : 0;
  const lonHi = lonValues.length ? Math.max(...lonValues) : 0;
  const latLo = latValues.length ? Math.min(...latValues) : 0;
  const latHi = latValues.length ? Math.max(...latValues) : 0;
  const lonPad = Math.max(0.5, (lonHi - lonLo) * 0.1);
  const latPad = Math.max(0.5, (latHi - latLo) * 0.1);

  const axisLabel = { color: AXIS_LABEL_COLOR, fontFamily: FONT, fontSize: 12, margin: 10 };
  const axisLine = { lineStyle: { color: AXIS_LINE_COLOR } };
  const splitLine = { lineStyle: { color: SPLIT_LINE_COLOR } };

  const hasValue = valIdx >= 0;
  return {
    color: [palette[0]],
    textStyle: { fontFamily: FONT },
    grid: { top: 32, left: 64, right: 24, bottom: hasValue ? 64 : 40, containLabel: true },
    tooltip: {
      trigger: "item" as const,
      textStyle: { fontFamily: FONT, fontSize: 12, color: "#171717" },
      borderColor: AXIS_LINE_COLOR,
      backgroundColor: "rgba(255,255,255,0.96)",
      extraCssText: "box-shadow: 0 6px 24px rgba(15,23,42,0.08); border-radius: 10px;",
      formatter: (params: unknown) => {
        const p = params as { value: [number, number, number]; name: string };
        const [lon, lat, v] = p.value;
        const head = p.name ? `<div style="font-weight:600;color:#0f172a">${escapeHtml(p.name)}</div>` : "";
        const coord = `<div style="margin-top:${p.name ? 4 : 0}px;color:#404040">${lat.toFixed(4)}°N, ${lon.toFixed(4)}°E</div>`;
        const val = hasValue ? `<div style="margin-top:4px;color:#0f172a"><b>${v.toLocaleString()}</b></div>` : "";
        return head + coord + val;
      },
    },
    xAxis: {
      type: "value" as const,
      name: "Longitude",
      nameLocation: "middle" as const,
      nameGap: 28,
      nameTextStyle: { fontFamily: FONT, fontSize: 12, color: AXIS_LABEL_COLOR },
      min: lonLo - lonPad,
      max: lonHi + lonPad,
      axisLabel: { ...axisLabel, formatter: (v: number) => `${v.toFixed(2)}°E` },
      axisLine,
      splitLine,
    },
    yAxis: {
      type: "value" as const,
      name: "Latitude",
      nameLocation: "middle" as const,
      nameGap: 44,
      nameTextStyle: { fontFamily: FONT, fontSize: 12, color: AXIS_LABEL_COLOR },
      min: latLo - latPad,
      max: latHi + latPad,
      axisLabel: { ...axisLabel, formatter: (v: number) => `${v.toFixed(2)}°N` },
      axisLine,
      splitLine,
    },
    // Show the visualMap (color + size legend) only when a value column
    // was provided. Without a value, all points are uniform.
    ...(hasValue
      ? {
          visualMap: {
            min: vMin,
            max: vMax,
            dimension: 2,
            calculable: true,
            orient: "horizontal" as const,
            left: "center",
            bottom: 8,
            textStyle: { fontFamily: FONT, fontSize: 11, color: "#525252" },
            inRange: {
              color: ["#eef2ff", "#c7d2fe", "#818cf8", "#4f46e5", "#312e81"],
              symbolSize: [6, 24],
            },
          },
        }
      : {}),
    series: [
      {
        type: "scatter" as const,
        data: points,
        symbolSize: hasValue ? undefined : 8,
        itemStyle: { opacity: 0.6 },
        emphasis: { itemStyle: { opacity: 1, shadowBlur: 6, shadowColor: "rgba(0,0,0,0.2)" } },
      },
    ],
  };
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}
